import _ from 'underscore';
import { Component, createRef } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '@workramp/ui';
import { apiPost } from '~/common';
import { sanitizeUserInput } from '~/utils/sanitize-user-input';
import { media, theme } from '../../../styles';
import UniqueEmailInput from '../../common/unique-email-input';
import AcademyThemeWrapper from '../common/academy-theme-wrapper';
import ColorUtils from '../../../common/color-utils';
import WRButton from '../../widget/wr-button';
import { SettingCheckbox } from '../../common/settings';

class AcademyLearnerLogin extends Component {
  TRANSLATED_LABEL_EMAIL = I18n.t('js.academy.login_register.email');

  TRANSLATED_LABEL_PASSWORD = I18n.t('js.academy.login_register.password');

  TRANSLATED_LABEL_FULL_NAME = I18n.t('js.academy.login_register.full_name');

  TRANSLATED_BUTTON_CONTINUE = I18n.t('js.academy.login_register.button.continue');

  TRANSLATED_BUTTON_LOGIN = I18n.t('js.academy.login_register.button.login');

  TRANSLATED_BUTTON_REGISTER = I18n.t('js.academy.login_register.button.register');

  TRANSLATED_FOOTER_CREATE_ACCOUNT = I18n.t('js.academy.login_register.footer.create_account');

  TRANSLATED_FOOTER_LOGIN = I18n.t('js.academy.login_register.footer.login');

  TRANSLATED_EMAIL_IN_USE = I18n.t('js.academy.login_register.email_in_use');

  TRANSLATED_CREATE_PASSWORD = I18n.t('js.academy.login_register.create_password');

  TRANSLATED_SELECT = I18n.t('common.select');

  TRANSLATED_FORGOT_PASSWORD = I18n.t('js.academy.login_register.footer.forgot_password');

  constructor(props) {
    super(props);

    const academyName = props.academy.title;
    const { allowLoginViaSsoOnly, passwordRequired } = props.customerLearningCloudHub;

    const TRANSLATED_LOGIN_HEADLINE = I18n.t('js.academy.login_register.headline.login', { name: academyName });
    const TRANSLATED_REGISTER_HEADLINE = I18n.t('js.academy.login_register.headline.register', {
      name: academyName,
    });
    let checkboxStates = {};

    // form is uncontrolled but we need to track checkbox fields
    // so we can submit values regardless of checked status
    if (props.registrationFields)
      checkboxStates = props.registrationFields
        .filter(({ fieldType }) => APP_CONSTANTS.ACADEMY_REGISTRATION_FIELD_TYPES.CHECKBOX.DB_VALUE === fieldType)
        .reduce((checkboxes, registrationField) => {
          checkboxes[registrationField.name] = false;
          return checkboxes;
        }, {});

    this.state = {
      ...checkboxStates,
      termsAccepted: null,
    };

    this.csrf = document.head.querySelector('[name=csrf-token][content]')?.content;
    this.showPasswordField = !allowLoginViaSsoOnly && passwordRequired;
    this.showLogin = props.isLogin && !props.emailLoginCodeSent;
    if (props.isLogin) {
      this.buttonText = allowLoginViaSsoOnly ? this.TRANSLATED_BUTTON_CONTINUE : this.TRANSLATED_BUTTON_LOGIN;
    } else if (props.isReturnToTraining || props.areAdditionalFieldsRequired) {
      this.buttonText = this.TRANSLATED_BUTTON_CONTINUE;
    } else {
      this.buttonText = this.TRANSLATED_BUTTON_REGISTER;
    }
    this.headline = props.headline || (props.isLogin ? TRANSLATED_LOGIN_HEADLINE : TRANSLATED_REGISTER_HEADLINE);
    this.formAction = props.isLogin ? '/login' : '/register';
    this.showFooter = (!props.isLogin || !allowLoginViaSsoOnly) && !props.hideFooter;
    this.footerText = props.isLogin ? this.TRANSLATED_FOOTER_CREATE_ACCOUNT : this.TRANSLATED_FOOTER_LOGIN;
    this.footerLink = props.isLogin ? '/register' : '/login';
    this.showRegister = !props.isLogin && !props.emailLoginCodeSent;
    this.termsAcceptanceRequired = this.props.academy.themeOptions.requireExplicitTermsAndConditionsAgreement;
    this.requiredFieldNamesForSSO = this.termsAcceptanceRequired && !props.isLogin ? ['terms'] : [];

    this.themeOptions = this.props.academy.themeOptions;
    this.buttonBackgroundColor = this.themeOptions.loginRegisterButtonBackgroundColor;
    this.buttonHoverColor = ColorUtils.lightenDarkenColor(this.buttonBackgroundColor, 15);
    this.buttonTextColor = ColorUtils.getTextColorForHex(this.buttonBackgroundColor);

    const requiredRegistrationFields = _.filter(this.props.registrationFields, (field) => field.required);
    this.requiredFieldNamesForLoginOrRegister = [
      ..._.map(requiredRegistrationFields, (field) => field.name),
      ...this.requiredFieldNamesForSSO,
    ];

    this.emailRef = createRef();
    this.formRef = createRef();
  }

  componentDidMount() {
    $(this.emailRef.current).focus();
  }

  render() {
    const { academy, emailLoginCodeSent, errorMsg, hideFooter, isLogin, showSsoLoginOption } = this.props;
    const registrationLogo = academy.registrationLogo || '/images/logo_new.png';
    const registrationBackgroundImage =
      academy.registrationBackgroundImage || APP_CONSTANTS.DEFAULT_COVER_IMAGES.ACADEMY_REGISTRATION;
    const { themeOptions, allowLoginViaSsoOnly } = academy;

    const termsToDisplay = isLogin ? themeOptions.loginTermsAndConditions : themeOptions.termsAndConditions;
    const termsToDisplayBlank = termsToDisplay && termsToDisplay.trim() === '';
    const showTerms = !emailLoginCodeSent && _.isEmpty(this.props.ssoParams) && !hideFooter && !termsToDisplayBlank;
    const showRegisterFooter = this.showRegister && !hideFooter && _.isEmpty(this.props.ssoParams);
    const ssoOnlyErrorMsg = allowLoginViaSsoOnly && errorMsg;
    const showForm = !this.props.authCode && !this.props.emailLoginCodeSent && !ssoOnlyErrorMsg;
    const showAuthCodeContinue = !!this.props.authCode;
    const showSSOUser = !_.isEmpty(this.props.ssoParams) && this.props.ssoParams.email;

    return (
      <AcademyThemeWrapper
        hasLogo={academy.logo}
        backgroundImage={registrationBackgroundImage}
        style={{ paddingTop: '75px', paddingBottom: '75px' }}
        minFullHeight
      >
        <LoginWrapper
          buttonBackgroundColor={this.buttonBackgroundColor}
          buttonHoverBackgroundColor={this.buttonHoverColor}
          buttonTextColor={this.buttonTextColor}
          linkColor={themeOptions.loginRegisterLinkColor}
        >
          {showSSOUser && this.renderSsoUser()}
          <Logo src={registrationLogo} />
          {this.renderHeader()}
          {this.renderError()}
          {showForm && this.renderForm()}
          {showAuthCodeContinue && this.renderAuthCodeContinueButton()}
          {showRegisterFooter && this.renderRegisterFooter(showTerms ? '10px' : '20px')}
        </LoginWrapper>
      </AcademyThemeWrapper>
    );
  }

  renderTerms = (terms) => {
    const { requireExplicitTermsAndConditionsAgreement } = this.props.academy.themeOptions;
    const showCheckbox = !this.showLogin && requireExplicitTermsAndConditionsAgreement;

    return (
      <TermsWrapper>
        {showCheckbox && (
          <StyledSettingCheckbox
            name="terms"
            required
            onClickHandler={() => this.setState({ termsAccepted: !this.state.termsAccepted })}
          />
        )}
        <TermsBlock dangerouslySetInnerHTML={{ __html: sanitizeUserInput(terms, { allowSafeTags: true }) }} />
      </TermsWrapper>
    );
  };

  renderSsoUser = () => {
    const TRANSLATED_SSO_USER_MESSAGE = I18n.t('js.academy.login_register.sso_message', {
      email: this.props.ssoParams.email,
    });
    return <SsoUserWrapper dangerouslySetInnerHTML={{ __html: TRANSLATED_SSO_USER_MESSAGE }} />;
  };

  renderHeader() {
    return <Headline>{this.headline}</Headline>;
  }

  renderForm = () => {
    const {
      academy,
      emailLoginCodeSent,
      errorMsg,
      hideFooter,
      isLogin,
      showSsoLoginOption,
      ssoButtonText,
      ssoInitRoute,
      ssoParams,
    } = this.props;
    const { themeOptions, allowLoginViaSsoOnly } = academy;
    const termsToDisplay = isLogin ? themeOptions.loginTermsAndConditions : themeOptions.termsAndConditions;
    const termsToDisplayBlank = termsToDisplay && termsToDisplay.trim() === '';
    let showTerms = !emailLoginCodeSent && !hideFooter && !termsToDisplayBlank;
    if (!_.isEmpty(ssoParams)) {
      showTerms = showTerms && this.termsAcceptanceRequired;
    }

    const ssoOnlyErrorMsg = allowLoginViaSsoOnly && errorMsg;

    return (
      <form action={this.formAction} method="POST" ref={this.formRef} onSubmit={this.handleFormSubmit}>
        {showSsoLoginOption && this.renderSSOButton(ssoInitRoute, ssoButtonText, !ssoOnlyErrorMsg)}
        {this.showLogin ? this.renderLoginFields() : this.renderRegistrationFields()}
        <input name="authenticity_token" type="hidden" value={this.csrf} />
        {this.renderSubmitButton()}
        {this.showLogin && this.renderLoginFormFooter()}
        {showTerms && this.renderTerms(termsToDisplay)}
      </form>
    );
  };

  renderLoginFields() {
    return (
      <div>
        <BlockInput
          type="email"
          name={APP_CONSTANTS.ACADEMY_DEFAULT_REGISTRATION_FIELDS.EMAIL.DB_VALUE}
          ref={this.emailRef}
          placeholder={this.TRANSLATED_LABEL_EMAIL}
          aria-label={this.TRANSLATED_LABEL_EMAIL}
          aria-invalid={!!this.props.errorMsg}
          aria-describedby="login-error"
        />
        {this.showPasswordField && (
          <BlockInput
            type="password"
            name={APP_CONSTANTS.ACADEMY_DEFAULT_REGISTRATION_FIELDS.PASSWORD.DB_VALUE}
            placeholder={this.TRANSLATED_LABEL_PASSWORD}
            aria-label={this.TRANSLATED_LABEL_PASSWORD}
          />
        )}
      </div>
    );
  }

  renderRegistrationFields = () =>
    this.props.registrationFields.map((field) => {
      switch (field.fieldType) {
        case APP_CONSTANTS.ACADEMY_REGISTRATION_FIELD_TYPES.EMAIL.DB_VALUE:
          return this.renderEmailField(field, this.TRANSLATED_LABEL_EMAIL);
        case APP_CONSTANTS.ACADEMY_REGISTRATION_FIELD_TYPES.PASSWORD.DB_VALUE:
        case APP_CONSTANTS.ACADEMY_REGISTRATION_FIELD_TYPES.PHONE.DB_VALUE:
        case APP_CONSTANTS.ACADEMY_REGISTRATION_FIELD_TYPES.TEXT.DB_VALUE:
          return this.renderInputField(field);
        case APP_CONSTANTS.ACADEMY_REGISTRATION_FIELD_TYPES.DROPDOWN.DB_VALUE:
          return this.renderSelectField(field);
        case APP_CONSTANTS.ACADEMY_REGISTRATION_FIELD_TYPES.TEXTAREA.DB_VALUE:
          return this.renderTextAreaField(field);
        case APP_CONSTANTS.ACADEMY_REGISTRATION_FIELD_TYPES.CHECKBOX.DB_VALUE:
          return this.renderCheckboxField(field);
        default:
          return this.renderInputField(field);
      }
    });

  renderEmailField = (field, localizedEmailLabel) => {
    const { productInvitation } = this.props;
    const isDefault = field.isDefaultField === true;
    if (isDefault && getUrlParameters().prevent_default !== 'true') {
      return (
        <UniqueEmailInputWrapper>
          <InputLabel>
            {localizedEmailLabel}
            {field.required ? '*' : ''}
          </InputLabel>
          <UniqueEmailInput
            emailValidateUrl="unique_email?email="
            isUniqueCb={this.handleIsEmailUniqueResponse}
            readonly={productInvitation}
            value={productInvitation?.email}
          />
          {this.state.emailNotUnique && (
            <EmailNotUniqueText dangerouslySetInnerHTML={{ __html: this.TRANSLATED_EMAIL_IN_USE }} />
          )}
        </UniqueEmailInputWrapper>
      );
    }

    return this.renderInputField(field);
  };

  renderInputField = (field) => {
    let fieldLabel;
    if (
      field.fieldType === APP_CONSTANTS.ACADEMY_REGISTRATION_FIELD_TYPES.PASSWORD.DB_VALUE &&
      field.isDefaultField &&
      getExternalUser()
    ) {
      fieldLabel = this.TRANSLATED_CREATE_PASSWORD;
    } else if (field.isAcademyPasswordField === true) {
      fieldLabel = this.TRANSLATED_LABEL_PASSWORD;
    } else if (field.isAcademyNameField === true) {
      fieldLabel = this.TRANSLATED_LABEL_FULL_NAME;
    } else {
      fieldLabel = field.displayName;
    }

    return (
      <div key={field.name}>
        <InputLabel>
          {fieldLabel}
          {field.required ? '*' : ''}
        </InputLabel>
        <LabeledInput name={field.name} required={field.required} type={field.fieldType} />
      </div>
    );
  };

  //
  renderCheckboxField = (field) => (
    // unchecked input values are not sent to the server
    // using hidden input to ensure we capture user's choice
    <CheckboxContainer key={field.name}>
      <Checkbox
        type="checkbox"
        name={field.name}
        value="true"
        required={field.required}
        onChange={(e) => this.setState({ [e.target.name]: !this.state[e.target.name] })}
      />
      {!this.state[field.name] && <input name={field.name} type="hidden" value="false" />}
      <CheckboxLabel>
        {field.displayName}
        {field.required ? '*' : ''}
      </CheckboxLabel>
    </CheckboxContainer>
  );

  renderSelectField = (field) => (
    <div key={field.name}>
      <InputLabel>
        {field.displayName}
        {field.required ? '*' : ''}
      </InputLabel>
      <LabeledSelect name={field.name} required={field.required}>
        <option value="">{this.TRANSLATED_SELECT}</option>
        {field.data.map((option) => (
          <option value={option}>{option}</option>
        ))}
      </LabeledSelect>
    </div>
  );

  renderTextAreaField = (field) => (
    <div key={field.name}>
      <InputLabel>
        {field.displayName}
        {field.required ? '*' : ''}
      </InputLabel>
      <LabeledTextarea name={field.name} required={field.required} />
    </div>
  );

  applyOrRemoveRequiredFields = ({ isSubmitButtonClick, isSSOButtonClick }) => {
    // If some other button clicked, do nothing
    if (!isSubmitButtonClick && !isSSOButtonClick) return true;

    _.chain(this.formRef.current.elements)
      .filter((el) => _.includes(this.requiredFieldNamesForLoginOrRegister, el.name))
      .each((el) => {
        if (isSubmitButtonClick) {
          el.setAttribute('required', true);
        } else if (isSSOButtonClick) {
          el.name === 'terms' ? el.setAttribute('required', true) : el.removeAttribute('required');
        }
      });
    return true;
  };

  renderSubmitButton() {
    return (
      <SubmitButton
        isSSO={this.props.showSsoLoginOption}
        type="submit"
        onClick={() => this.applyOrRemoveRequiredFields({ isSubmitButtonClick: true })}
        disabled={this.state.emailNotUnique}
      >
        {this.buttonText}
      </SubmitButton>
    );
  }

  renderLoginFormFooter() {
    if (this.showFooter) {
      return (
        <LoginFooter className="login-forgot-password">
          <a href={this.footerLink} className="link-blue-new">
            {this.footerText}
          </a>
          {this.showPasswordField && (
            <a href="/forgot_password" className="link-blue-new">
              {this.TRANSLATED_FORGOT_PASSWORD}
            </a>
          )}
        </LoginFooter>
      );
    }
  }

  renderAuthCodeContinueButton = () => {
    const onClick = () => {
      apiPost(
        `/login/${this.props.authCode}`,
        {},
        (response) => {
          window.location.href = response.redirect;
        },
        () => {},
        '',
      );
    };

    return (
      <SubmitButton type="submit" onClick={onClick}>
        {this.TRANSLATED_BUTTON_CONTINUE}
      </SubmitButton>
    );
  };

  renderRegisterFooter = (marginTop) => (
    <FooterLinkWrapper marginTop={marginTop}>
      <FooterLink href={this.footerLink} className="link-blue-new">
        {this.footerText}
      </FooterLink>
    </FooterLinkWrapper>
  );

  renderError = () => {
    if (this.props.errorMsg) {
      return (
        <div className="login-msg login-error" id="login-error">
          {this.props.errorMsg}
        </div>
      );
    }
  };

  renderSSOButton = (buttonUrl, signInButtonTxt, showDivider) => {
    const defaultBtnText = I18n.t('js.academy.login_register.sso_sign_in');

    const SSOButton = () => {
      if (this.termsAcceptanceRequired && !this.showLogin && !this.state.termsAccepted) {
        return (
          <ButtonWithIcon
            backgroundColor={this.buttonBackgroundColor}
            hoverBackgroundColor={this.buttonHoverColor}
            textColor={this.buttonTextColor}
            submit
            type="submit"
            allowDefaultOnClick
            onClick={() => {
              this.applyOrRemoveRequiredFields({ isSSOButtonClick: true });
            }}
          >
            <Icon name="lock-lock:regular" />
            <ButtonText>{signInButtonTxt || defaultBtnText}</ButtonText>
          </ButtonWithIcon>
        );
      }
      return (
        <ButtonWithIcon
          backgroundColor={this.buttonBackgroundColor}
          hoverBackgroundColor={this.buttonHoverColor}
          textColor={this.buttonTextColor}
          href={`${buttonUrl}?`}
        >
          <Icon name="lock-lock:regular" />
          <ButtonText>{signInButtonTxt || defaultBtnText}</ButtonText>
        </ButtonWithIcon>
      );
    };

    return (
      <div>
        <SSOButton />
        {showDivider && (
          <SSODivider>
            <OrSpan>{I18n.t('common.application.or')}</OrSpan>
          </SSODivider>
        )}
      </div>
    );
  };

  handleIsEmailUniqueResponse = (isUnique) => {
    this.setState({ emailNotUnique: !isUnique });
  };

  handleFormSubmit = (e) => {
    if (getUrlParameters().prevent_default === 'true') {
      e.preventDefault();
    }
  };
}

const sharedButtonStyles = css`
  width: 100% !important;
  height: 44px !important;
  border-radius: 8px !important;
  box-sizing: border-box;
`;

const FooterLink = styled.a`
  text-align: center;
  font-size: 0.85rem;
`;

const FooterLinkWrapper = styled.div`
  margin-top: ${(props) => props.marginTop};
`;

const SSODivider = styled.p`
  text-align: center;
  border-bottom: 1px solid #ededf4;
  line-height: 0.1rem;
  margin: 2rem 0;
`;

const OrSpan = styled.span`
  color: #747487;
  padding: 0 12px;
  background-color: #fff;
  font-size: 14px;
`;

const ButtonText = styled.span`
  display: inline-block;
`;

const ButtonWithIcon = styled(WRButton)`
  ${sharedButtonStyles}
  background-color: ${(props) => props.backgroundColor};
  & * {
    color: ${(props) => props.textColor};
  }
  > svg {
    width: 20px;
    height: 20px;
    margin-right: 10px;
  }

  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: ${(props) => props.hoverBackgroundColor};
    opacity: 1;
  }
`;

const LoginWrapper = styled.main`
  text-align: center;
  max-width: 545px;
  margin: 0 auto;
  position: relative;
  box-shadow: 0 0 135px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  padding: 50px;
  background-color: #fff;
  border-radius: 4px;

  ${media.mobile} {
    width: 85%;
    max-width: 85%;
    padding: 50px 25px;
  }

  input,
  select,
  textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 0 15px;
    height: 45px;
    margin: 25px 0;
    border: 1px solid #e3e4e6;
    border-radius: 4px;
    background-color: #fff;
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin: 0 15px 0 0;
  }

  select,
  textarea {
    display: block;
  }

  select {
    text-indent: 5px;
  }

  textarea {
    padding: 15px;
    min-width: 100%;
    max-width: 100%;
    min-height: 100px;
  }

  button {
    background-color: ${(props) => props.buttonBackgroundColor};
    color: ${(props) => props.buttonTextColor};
    border: none;
    height: 50px;
    padding: 0 50px;
    box-sizing: border-box;
    border-radius: 4px;
    transition: all 0.3s ease-in-out;
    margin-bottom: 25px;
  }

  button:hover {
    background-color: ${(props) => props.buttonHoverBackgroundColor};
  }

  a {
    color: ${(props) => props.linkColor} !important;
  }

  ${ButtonWithIcon} {
    margin-bottom: 25px !important;
  }
`;

const Logo = styled.img`
  max-width: 50%;
  height: auto;
  padding-bottom: 2rem;

  ${media.mobile} {
    max-width: 75%;
  }
`;

const InputLabel = styled.div`
  color: #324050;
  ${theme.font.semibold}
  text-align: left;
  margin-bottom: 10px;
`;

const BlockInput = styled.input`
  display: block;
`;

const LabeledInput = styled.input`
  display: block;
  margin-top: 0 !important;
`;

const CheckboxLabel = styled.div`
  color: #324050;
  text-align: left;
  margin-bottom: 10px;
`;

const LabeledSelect = styled.select`
  margin-top: 0 !important;
`;

const LabeledTextarea = styled.textarea`
  margin-top: 0 !important;
`;

const CheckboxContainer = styled.div`
  margin-top: 0 !important;
  font-size: 16px;
  font-weight: 400;
  display: flex;
  align-items: start;
  padding-bottom: 16px;
`;

const Checkbox = styled.input`
  margin: 0;
  padding: 0;
  flex-shrink: 0;
`;

const Headline = styled.div`
  font-size: 1.2rem;
  color: #4e4f51;
  padding-bottom: 1.5rem;
`;

const EmailNotUniqueText = styled.div`
  color: red;
  font-size: 0.8rem;
  text-align: left;
  margin-top: 5px;
`;

const UniqueEmailInputWrapper = styled.div`
  position: relative;
  margin: 1rem 0 25px;

  input {
    margin: 0;
  }
`;

const LoginFooter = styled.div`
  margin: 20px 0 0 0;
  padding: 0;
  ${media.mobile} {
    flex-wrap: wrap;
    text-align: center;
    a {
      display: block;
      width: 100%;
      margin-bottom: 5px;
    }
  }
`;

const SsoUserWrapper = styled.div`
  font-size: 0.875rem;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const TermsBlock = styled.div`
  text-align: center;
  font-size: 0.85rem;

  > p {
    margin: 0px;
  }
`;

const SubmitButton = styled.button`
  ${sharedButtonStyles}
  ${(props) =>
    props.isSSO &&
    `
  border: 1px solid #babacc !important;
  background-color: #fff !important;
  color: #5b5f62 !important;
  opacity: 0.9;
  &:hover{
  opacity: 1;
  }
`}
  ${(props) => props.disabled && 'background-color: #bbb !important; cursor: not-allowed !important;'}
  margin-bottom: 0 !important;
  text-transform: none !important;
`;

const TermsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0px 10px;
  padding: 0 15px;
`;

const StyledSettingCheckbox = styled(SettingCheckbox)`
  width: 14px !important;
  height: 14px !important;
  margin: 0 8px 0 0 !important;
  flex-shrink: 0;
`;

export default AcademyLearnerLogin;
